import { useNavigate } from 'react-router-dom'
import React, { useEffect, useState, useCallback, useMemo, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import ContainerPage from "../../components/ContainerPage/ContainerPage"
import PageTitle from "../../components/PageTitle/PageTitle"
import { useMyContextLoading } from "../../components/LoadingContextProvider/LoadingContextProvider"
import { getListCache, setListCache, buildCacheKey } from "../../utils/listCache"
import { showSnackBar } from "../../components/Snackbar/Snackbar"
import { useOfficeVisibility } from '../../hooks/useOfficeVisibility'
import usePinnedFeatureFlag from '../../hooks/usePinnedFeatureFlag'
import { selectFeatureFlagsSettled } from '../../features/featureFlags/selectors'
import { DASHBOARD_DRILLDOWN_LINKS, DASHBOARD_CREDIT_PULLS } from '../../config/featureFlagKeys'
import { StorageGetItem, USER } from "../../lib/storage"
import { formatMoney } from "../../utils/amount"
import { formatBusinessDateTime } from "../../utils/date"
import useDashboardUrlView from './useDashboardUrlView'
import useDashboardScroll from './useDashboardScroll'
import { applyDashboardChange, rangeOf, viewOf, EMPTY_RANGE } from './dashboardView'
import { metricOptionsFor } from './creditPullsMetric'

import {
  getSalesDashboardMetrics,
  getAffiliatesForDashboard,
  getContactGroupsForDashboard,
  getChartData,
  getCreditPullLeaderboard,
  getForecasting,
  getDrilldownRows,
} from "../../features/salesDashboard/salesDashboard"

import { LuUserPlus, LuDollarSign, LuUserX, LuPercent, LuUndo2, LuBarChart3 } from "react-icons/lu"
import MetricCard from "./components/MetricCard"
import DateRangeFilter from "./components/DateRangeFilter"
import AffiliateFilter from "./components/AffiliateFilter"
import ContactGroupFilter from "./components/ContactGroupFilter"
import MetricsChart, { METRIC_OPTIONS, MONEY_METRICS, DRILLABLE_METRICS, SNAPSHOT_METRICS } from "./components/MetricsChart"
import { daysInTransit, EMPTY_CELL } from "./components/drilldownColumns"
import ClearanceFunnelChart from "./components/ClearanceFunnelChart"
import DrilldownPanel from "./components/DrilldownPanel"
import CreditPullLeaderboard from "./components/CreditPullLeaderboard"
import { collectionRateCardProps } from "./components/CollectionRateSplit"

// Card sparklines are sourced from the real chart endpoint, so only metric types
// /sales-dashboard/chart actually serves (METRIC_OPTIONS) are ever requested — and only
// the ones a card is actually drawn for. Collection Rate is a ratio with no series of its
// own. Returned Payments joined the list once the endpoint gained getReturnedPaymentsByDay;
// before that its switch fell through to clearedPayments and would have plotted the wrong
// metric, which is also why that fallback is now a 400.
const SPARK_METRICS = ['newEnrollments', 'clearedPayments', 'totalCanceled', 'returnedPayments']
const EMPTY_SPARKLINES = { newEnrollments: [], clearedPayments: [], totalCanceled: [], returnedPayments: [] }

const contactPath = (clientId) => `/contact/${clientId}`

const PERIOD_LABELS = {
  today: 'Today',
  last7Days: 'Last 7 days',
  last30Days: 'Last 30 days',
  custom: 'Custom range',
}

const SalesDashboard = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { triggerBar } = useMyContextLoading()

  const flagsSettled = useSelector(selectFeatureFlagsSettled)
  const { enabled: urlMode } = usePinnedFeatureFlag(DASHBOARD_DRILLDOWN_LINKS)
  const { enabled: creditPullsOn } = usePinnedFeatureFlag(DASHBOARD_CREDIT_PULLS)
  const metricOptions = metricOptionsFor(creditPullsOn)
  const dashboardUrl = useDashboardUrlView({ metricOptions })
  const urlView = dashboardUrl.view
  const [seeded, setSeeded] = useState(false)
  const viewPending = !flagsSettled && dashboardUrl.hasView
  const fromUrl = (urlMode && (dashboardUrl.hasView || seeded)) || viewPending

  // User data
  const [storedRole, setStoredRole] = useState('')
  const userRole = viewPending ? '' : storedRole
  const [isGroupOwner, setIsGroupOwner] = useState(false)
  const [isSupervisor, setIsSupervisor] = useState(false)
  const [accessLevel, setAccessLevel] = useState(undefined)
  const [currentUser, setCurrentUser] = useState(null)

  // Check if user can see filters
  const canSeeContactGroupFilter = userRole === 'customer' || isSupervisor || userRole === 'affiliateGroupsAdmin'
  const canSeeAffiliateFilter = userRole === 'customer' || isSupervisor || (userRole === 'affiliate' && isGroupOwner) || userRole === 'affiliateGroupsAdmin'

  // Filters
  const [localDatePreset, setLocalDatePreset] = useState('last30Days')
  const [localCustomDateRange, setLocalCustomDateRange] = useState(EMPTY_RANGE)
  const [localSelectedAffiliate, setLocalSelectedAffiliate] = useState('')
  const [localSelectedContactGroup, setLocalSelectedContactGroup] = useState('')
  const datePreset = fromUrl ? urlView.period : localDatePreset
  const customDateRange = fromUrl ? dashboardUrl.customDateRange : localCustomDateRange
  const selectedAffiliate = fromUrl ? (canSeeAffiliateFilter ? urlView.affiliate : '') : localSelectedAffiliate
  const selectedContactGroup = fromUrl ? (canSeeContactGroupFilter ? urlView.group : '') : localSelectedContactGroup

  // Dropdown options
  const [affiliates, setAffiliates] = useState([])
  const [contactGroups, setContactGroups] = useState([])
  const [loadingDropdowns, setLoadingDropdowns] = useState(false)
  const [optionsReadyFor, setOptionsReadyFor] = useState(undefined)
  const metricsRequestRef = useRef(0)
  const chartRequestRef = useRef(0)
  const sparklinesRequestRef = useRef(0)
  const forecastingRequestRef = useRef(0)
  const urlModeRef = useRef(urlMode)

  useEffect(() => {
    urlModeRef.current = urlMode
  }, [urlMode])
  const [metricsLoading, setMetricsLoading] = useState(true)

  // Metrics data (with comparison structure)
  const [metrics, setMetrics] = useState({
    newEnrollments: { current: 0, previous: 0, percentChange: 0 },
    clearedPayments: {
      current: { amount: 0, count: 0 },
      previous: { amount: 0, count: 0 },
      percentChange: { amount: 0, count: 0 },
    },
    totalPayments: {
      current: { amount: 0, count: 0 },
      previous: { amount: 0, count: 0 },
      percentChange: { amount: 0, count: 0 },
    },
    returnedPayments: {
      current: { amount: 0, count: 0, nsf: 0, declined: 0 },
      previous: { amount: 0, count: 0, nsf: 0, declined: 0 },
      percentChange: { amount: 0, count: 0 },
    },
    pendingPayments: {
      current: { amount: 0, count: 0 },
      previous: { amount: 0, count: 0 },
      percentChange: { amount: 0, count: 0 },
    },
    inTransitPayments: {
      current: { amount: 0, count: 0 },
      oldestSubmittedAt: null,
      byProcessor: [],
      asOf: null,
    },
    collectionRate: null,
    clearedRatio: null,
    cancelledRatio: null,
    totalCanceled: { current: 0, previous: 0, percentChange: 0 },
    averageLifeSpan: 0,
  })

  // Chart data
  const [chartData, setChartData] = useState([])
  const [localChartMetricType, setLocalChartMetricType] = useState('clearedPayments')
  const chartMetricType = fromUrl ? urlView.metric : localChartMetricType
  const [chartError, setChartError] = useState(false)
  const [leaderboard, setLeaderboard] = useState(null)
  const [leaderboardLoading, setLeaderboardLoading] = useState(false)
  const [leaderboardError, setLeaderboardError] = useState(false)
  const leaderboardRequestRef = useRef(0)

  // Per-card sparkline series
  const [sparklines, setSparklines] = useState({ ...EMPTY_SPARKLINES })

  const [localDetailsView, setLocalDetailsView] = useState('chart')
  const [localDrilldownDay, setLocalDrilldownDay] = useState(null)
  const [drilldownRows, setDrilldownRows] = useState([])
  const [drilldownMeta, setDrilldownMeta] = useState(null)
  const [drilldownLoading, setDrilldownLoading] = useState(false)
  const [drilldownError, setDrilldownError] = useState(null)
  const [localDrilldownSortOrder, setLocalDrilldownSortOrder] = useState('desc')
  const [localDrilldownPage, setLocalDrilldownPage] = useState(1)
  const [localDrilldownLimit, setLocalDrilldownLimit] = useState('25')
  const detailsView = fromUrl ? urlView.view : localDetailsView
  const drilldownDay = fromUrl ? (urlView.day || null) : localDrilldownDay
  const drilldownSortOrder = fromUrl ? urlView.sort : localDrilldownSortOrder
  const drilldownPage = fromUrl ? urlView.page : localDrilldownPage
  const drilldownLimit = fromUrl ? urlView.limit : localDrilldownLimit
  const [drilldownLoadedScope, setDrilldownLoadedScope] = useState(null)
  const drilldownScopeRef = useRef(null)
  const drilldownPeriodRef = useRef(null)
  const drilldownRequestRef = useRef(0)

  // Reps (regular affiliates without group ownership) have no accounting access, so they
  // must not see the cleared/total payments running totals. isGroupOwner is loaded from
  // storage below; the backend enforces the same gate (defense in depth).
  const { level: dashboardVisibility } = useOfficeVisibility(currentUser)
  const isRep = userRole === 'affiliate' && !isGroupOwner
  const ratiosOnly = dashboardVisibility === 'RATIOS_ONLY'
  const deniesMoney = isRep || ratiosOnly || accessLevel === 'fullContactsNoAccounting'
  const canChartMetric = (metric) => !(deniesMoney && MONEY_METRICS.includes(metric))
  const effectiveMetricOf = useCallback(
    (metric) => (deniesMoney && MONEY_METRICS.includes(metric) ? 'newEnrollments' : metric),
    [deniesMoney],
  )
  const effectiveChartMetric = effectiveMetricOf(chartMetricType)

  // Derived, not corrected after the fact: an effect keyed on the metric alone cannot undo a
  // view switch that happens later (a day-click on a non-drillable series did exactly that, and
  // the List|Chart toggle is hidden for those metrics, so the operator was stuck in an empty
  // list). Deriving it makes the invalid state unrepresentable.
  const effectiveView = DRILLABLE_METRICS.includes(effectiveChartMetric) ? detailsView : 'chart'

  // Forecasting data (average term + payment-position clearance funnel + retention curve)
  const [forecasting, setForecasting] = useState({
    averageTerm: null,
    planCount: 0,
    clearanceByPosition: [],
    retentionByPosition: [],
  })
  const [forecastingLoading, setForecastingLoading] = useState(true)

  // Load user data on mount
  useEffect(() => {
    StorageGetItem(USER).then(result => {
      setCurrentUser(result || null)
      setStoredRole(result?.role)
      setIsGroupOwner(result?.isGroupOwner)
      setIsSupervisor(result?.subRole?.value === 'supervisor')
      setAccessLevel(result?.accessLevel)
    })
  }, [])

  // Load dropdown options
  const fetchDropdownOptions = useCallback(async () => {
    if (!userRole) return

    setLoadingDropdowns(true)
    let groupsLoaded = true
    try {
      // Fetch contact groups (for customer/supervisor/affiliateGroupsAdmin)
      if (userRole === 'customer' || isSupervisor || userRole === 'affiliateGroupsAdmin') {
        const groupsResult = await dispatch(getContactGroupsForDashboard())
        if (groupsResult?.payload?.data) {
          setContactGroups(groupsResult.payload.data)
        }
        groupsLoaded = Boolean(groupsResult?.payload?.data)
      }

      // Fetch affiliates
      const affiliatesResult = await dispatch(getAffiliatesForDashboard({
        contactGroupId: selectedContactGroup
      }))
      if (affiliatesResult?.payload?.data) {
        setAffiliates(affiliatesResult.payload.data)
      }
      if (urlModeRef.current) {
        setOptionsReadyFor({
          group: selectedContactGroup,
          groupsLoaded,
          affiliatesLoaded: Boolean(affiliatesResult?.payload?.data),
        })
      }
    } catch (error) {
      console.error('Failed to fetch dropdown options:', error)
    }
    setLoadingDropdowns(false)
  }, [dispatch, userRole, isSupervisor, selectedContactGroup])

  useEffect(() => {
    fetchDropdownOptions()
  }, [fetchDropdownOptions])

  // Clear affiliate selection when contact group changes
  useEffect(() => {
    setLocalSelectedAffiliate('')
  }, [localSelectedContactGroup])

  const buildParams = useCallback(() => {
    const params = {
      datePreset,
      affiliateId: selectedAffiliate || undefined,
      contactGroupId: selectedContactGroup || undefined,
    }
    if (datePreset === 'custom' && customDateRange.startDate && customDateRange.endDate) {
      params.startDate = customDateRange.startDate.toISOString()
      params.endDate = customDateRange.endDate.toISOString()
    }
    return params
  }, [datePreset, selectedAffiliate, selectedContactGroup, customDateRange])

  const fetchMetrics = useCallback(async () => {
    if (!userRole) return
    const params = buildParams()
    const cacheKey = buildCacheKey('dashboard', params)
    const cached = getListCache(cacheKey)
    if (cached) {
      setMetrics(cached)
      setMetricsLoading(false)
    } else {
      setMetricsLoading(true)
    }
    triggerBar(true)
    const request = metricsRequestRef.current + 1
    metricsRequestRef.current = request
    const superseded = () => urlModeRef.current && request !== metricsRequestRef.current
    try {
      const metricsResult = await dispatch(getSalesDashboardMetrics(params))
      if (superseded()) return
      if (metricsResult?.error) {
        showSnackBar("error", metricsResult?.payload || 'Failed to fetch metrics')
      } else if (metricsResult?.payload?.data) {
        setMetrics(metricsResult.payload.data)
        setListCache(cacheKey, metricsResult.payload.data)
      }
    } catch (error) {
      if (superseded()) return
      showSnackBar('error', 'An unexpected error occurred')
    } finally {
      if (!superseded()) setMetricsLoading(false)
      triggerBar(false)
    }
  }, [dispatch, userRole, buildParams, triggerBar])

  const fetchChart = useCallback(async () => {
    if (!userRole) return
    const request = chartRequestRef.current + 1
    chartRequestRef.current = request
    try {
      const chartResult = await dispatch(getChartData({ ...buildParams(), metricType: effectiveChartMetric }))
      if (urlModeRef.current && request !== chartRequestRef.current) return
      if (chartResult?.error) {
        // Distinguish "request failed" from "period has no rows" so the chart can offer a retry.
        setChartError(true)
      } else if (chartResult?.payload?.data) {
        setChartData(chartResult.payload.data)
        setChartError(false)
      }
    } catch (error) {
      if (urlModeRef.current && request !== chartRequestRef.current) return
      setChartError(true)
      showSnackBar('error', 'An unexpected error occurred')
    }
  }, [dispatch, userRole, buildParams, effectiveChartMetric])

  const showLeaderboard = Boolean(creditPullsOn) && effectiveChartMetric === 'creditPulls'

  const fetchLeaderboard = useCallback(async () => {
    if (!userRole) return
    const request = leaderboardRequestRef.current + 1
    leaderboardRequestRef.current = request
    setLeaderboardLoading(true)
    setLeaderboardError(false)
    try {
      const result = await dispatch(getCreditPullLeaderboard(buildParams()))
      if (request !== leaderboardRequestRef.current) return
      if (result?.error) {
        setLeaderboardError(true)
      } else if (result?.payload?.data) {
        setLeaderboard(result.payload.data)
      }
    } catch (error) {
      if (request !== leaderboardRequestRef.current) return
      setLeaderboardError(true)
    }
    setLeaderboardLoading(false)
  }, [dispatch, userRole, buildParams])

  useEffect(() => {
    if (!showLeaderboard) {
      leaderboardRequestRef.current += 1
      return
    }
    if (datePreset === 'custom' && !(customDateRange.startDate && customDateRange.endDate)) return
    fetchLeaderboard()
  }, [showLeaderboard, fetchLeaderboard, datePreset, customDateRange])

  useEffect(() => {
    if (datePreset === 'custom') {
      if (customDateRange.startDate && customDateRange.endDate) {
        fetchMetrics()
      }
    } else {
      fetchMetrics()
    }
  }, [datePreset, customDateRange, selectedAffiliate, selectedContactGroup, userRole])

  // A snapshot metric reads nothing from the period, so an unfinished custom range is not a
  // reason to withhold it — for every other metric the server rejects that combination on purpose.
  const snapshotMetric = SNAPSHOT_METRICS.includes(effectiveChartMetric)

  useEffect(() => {
    if (datePreset === 'custom' && !snapshotMetric) {
      if (customDateRange.startDate && customDateRange.endDate) {
        fetchChart()
      }
    } else {
      fetchChart()
    }
  }, [datePreset, customDateRange, selectedAffiliate, selectedContactGroup, userRole, effectiveChartMetric, snapshotMetric])

  // The selection the panel is currently asking about. Page, limit and sort are deliberately not
  // part of it: they turn pages within one selection, and including them would blank the chips and
  // the period total on every page turn — the blinking the reset comment below exists to prevent.
  const rangeIncomplete = datePreset === 'custom'
    && !snapshotMetric
    && !(customDateRange.startDate && customDateRange.endDate)
  const drilldownScopeKey = useMemo(
    () => JSON.stringify([buildParams(), effectiveChartMetric, drilldownDay || null]),
    [buildParams, effectiveChartMetric, drilldownDay],
  )

  // Rows are only ever shown under the selection they were loaded for. Without this the guard
  // below silently skipped the fetch while the heading, columns, chips and caption still followed
  // the new selection, leaving one selection's records labelled as another's.
  const drilldownPending = drilldownLoadedScope !== drilldownScopeKey

  const metricsWithheld = datePreset === 'custom' && !(customDateRange.startDate && customDateRange.endDate)
  const scrollReady = (!metricsLoading || metricsWithheld) && !forecastingLoading
    && (effectiveView !== 'list' || (!drilldownLoading && (!drilldownPending || Boolean(drilldownError))))
  const { save: saveScroll } = useDashboardScroll({ enabled: urlMode, ready: scrollReady })

  const fetchDrilldown = useCallback(async () => {
    if (!userRole) return
    if (effectiveView !== 'list') return
    if (!DRILLABLE_METRICS.includes(effectiveChartMetric)) return
    // Same guard the metrics, chart and sparkline fetches carry: a custom preset is incomplete
    // until both ends are picked, and the server rejects that combination on purpose. Without it,
    // opening the date picker replaced the rows with an error while the cards kept their numbers.
    if (rangeIncomplete) return

    const periodKey = JSON.stringify([datePreset, customDateRange])
    const scopeKey = drilldownScopeKey
    const scopeChanged = drilldownScopeRef.current !== null && scopeKey !== drilldownScopeRef.current
    const periodChanged = drilldownPeriodRef.current !== null && periodKey !== drilldownPeriodRef.current

    // A reset below is already queued for this change, and effects in the same commit still see
    // the pre-reset state. Firing here would request the OLD page (or the old chart day) under
    // the NEW selection — a request whose only possible outcome is wrong. Let the reset land and
    // fetch on the next pass instead. The refs stay untouched so that pass still sees the change.
    if (!urlMode && ((scopeChanged && Number(drilldownPage) !== 1) || (periodChanged && drilldownDay))) return

    // The chips and the period total describe the whole selection, not the page on screen.
    // Dropping them on a page turn made them blink out; keeping them across a metric or
    // filter change would show the previous selection's figures. So: keep within a scope,
    // clear when the scope itself moves.
    const firstFetch = drilldownScopeRef.current === null
    drilldownScopeRef.current = scopeKey
    drilldownPeriodRef.current = periodKey

    // Responses are not ordered. Without this the reply that LANDS last wins, so a superseded
    // request can paint its rows under the current selection's columns, heading and pager —
    // figures that do not describe the rows beneath them, in a feature built to be audited.
    const requestId = drilldownRequestRef.current + 1
    drilldownRequestRef.current = requestId
    const isCurrent = () => drilldownRequestRef.current === requestId

    setDrilldownLoading(true)
    setDrilldownError(null)
    setDrilldownRows([])
    if (scopeChanged || firstFetch) setDrilldownMeta(null)
    try {
      const result = await dispatch(getDrilldownRows({
        ...buildParams(),
        metric: effectiveChartMetric,
        day: drilldownDay || undefined,
        sortOrder: drilldownSortOrder,
        limit: Number(drilldownLimit),
        offset: (Number(drilldownPage) - 1) * Number(drilldownLimit),
      }))

      if (!isCurrent()) return

      if (result?.error) {
        setDrilldownError(result?.payload || 'Failed to fetch records')
        setDrilldownMeta(null)
      } else if (result?.payload?.data) {
        setDrilldownRows(result.payload.data.rows || [])
        setDrilldownMeta(result.payload.data.meta || null)
        setDrilldownLoadedScope(scopeKey)
      }
    } catch (error) {
      if (!isCurrent()) return
      setDrilldownError('An unexpected error occurred')
      setDrilldownMeta(null)
    }
    if (isCurrent()) setDrilldownLoading(false)
  }, [
    dispatch, userRole, buildParams, effectiveView, effectiveChartMetric, datePreset, customDateRange,
    drilldownDay, drilldownSortOrder, drilldownPage, drilldownLimit, drilldownScopeKey, rangeIncomplete, urlMode,
  ])

  useEffect(() => {
    setLocalDrilldownDay(null)
  }, [localDatePreset, localCustomDateRange])

  // Sort belongs here too: flipping the order on page 7 otherwise lands on page 7 of a
  // reversed list, which reads as data loss rather than as a re-sort.
  useEffect(() => {
    setLocalDrilldownPage(1)
  }, [
    effectiveChartMetric, localDrilldownDay, localDrilldownLimit, localDrilldownSortOrder,
    localDatePreset, localCustomDateRange, localSelectedAffiliate, localSelectedContactGroup,
  ])

  // Declared AFTER the resets so a single commit applies them first. That alone is not enough
  // (this effect still closes over the pre-reset values), which is what the guard above is for.
  useEffect(() => {
    fetchDrilldown()
  }, [
    datePreset, customDateRange, selectedAffiliate, selectedContactGroup, userRole,
    effectiveView, effectiveChartMetric, drilldownDay, drilldownSortOrder, drilldownPage, drilldownLimit,
  ])


  const openDrilldownLocal = useCallback((metric) => {
    setLocalChartMetricType(metric)
    setLocalDrilldownDay(null)
    setLocalDetailsView('list')
  }, [])

  const openDrilldownForDayLocal = useCallback((day) => {
    setLocalDrilldownDay(day)
    setLocalDetailsView('list')
  }, [])

  const updateDashboardView = dashboardUrl.update
  const changeDashboardView = useCallback(
    (change) => updateDashboardView((current) => applyDashboardChange(current, change, { effectiveMetricOf })),
    [updateDashboardView, effectiveMetricOf],
  )

  const openDrilldown = urlMode ? (metric) => changeDashboardView({ type: 'openDrilldown', metric }) : openDrilldownLocal
  const openDrilldownForDay = urlMode ? (day) => changeDashboardView({ type: 'openDay', day }) : openDrilldownForDayLocal
  const changeDatePreset = urlMode ? (value) => changeDashboardView({ type: 'period', value }) : setLocalDatePreset
  const changeCustomDateRange = urlMode
    ? (next) => updateDashboardView((current) => applyDashboardChange(
      current,
      { type: 'range', value: typeof next === 'function' ? next(rangeOf(current)) : next },
      { effectiveMetricOf },
    ))
    : setLocalCustomDateRange
  const changeContactGroup = urlMode ? (value) => changeDashboardView({ type: 'group', value }) : setLocalSelectedContactGroup
  const changeAffiliate = urlMode ? (value) => changeDashboardView({ type: 'affiliate', value }) : setLocalSelectedAffiliate
  const changeChartMetric = urlMode ? (value) => changeDashboardView({ type: 'metric', value }) : setLocalChartMetricType
  const changeDetailsView = urlMode ? (value) => changeDashboardView({ type: 'view', value }) : setLocalDetailsView
  const clearDrilldownDay = urlMode ? () => changeDashboardView({ type: 'clearDay' }) : () => setLocalDrilldownDay(null)
  const changeDrilldownSortOrder = urlMode ? (value) => changeDashboardView({ type: 'sort', value }) : setLocalDrilldownSortOrder
  const changeDrilldownPage = urlMode ? (value) => changeDashboardView({ type: 'page', value }) : setLocalDrilldownPage
  const changeDrilldownLimit = urlMode ? (value) => changeDashboardView({ type: 'limit', value }) : setLocalDrilldownLimit

  // Load per-card sparkline series (parallel, non-blocking, decorative). Money metrics are
  // stripped for reps for the same reason the chart menu is (see isRep above).
  const fetchSparklines = useCallback(async () => {
    if (!userRole) return
    const base = buildParams()
    const cardMetrics = deniesMoney
      ? SPARK_METRICS.filter(mt => !MONEY_METRICS.includes(mt))
      : SPARK_METRICS
    const request = sparklinesRequestRef.current + 1
    sparklinesRequestRef.current = request
    try {
      const results = await Promise.all(
        cardMetrics.map(mt => dispatch(getChartData({ ...base, metricType: mt })))
      )
      if (urlModeRef.current && request !== sparklinesRequestRef.current) return
      const next = { ...EMPTY_SPARKLINES }
      cardMetrics.forEach((mt, i) => {
        const arr = results[i]?.payload?.data
        next[mt] = Array.isArray(arr) ? arr.map(p => p.value) : []
      })
      setSparklines(next)
    } catch (e) {
      // sparklines are decorative — ignore failures
    }
  }, [dispatch, userRole, deniesMoney, buildParams])

  useEffect(() => {
    if (datePreset === 'custom') {
      if (customDateRange.startDate && customDateRange.endDate) fetchSparklines()
    } else {
      fetchSparklines()
    }
  }, [datePreset, customDateRange, selectedAffiliate, selectedContactGroup, userRole])

  // Forecasting is a snapshot over the current client population — it is scoped by
  // affiliate/contact group only, not by the date range filter (no datePreset/custom
  // range dependency here, unlike fetchMetrics/fetchChart above).
  const fetchForecasting = useCallback(async () => {
    if (!userRole) return
    setForecastingLoading(true)
    const request = forecastingRequestRef.current + 1
    forecastingRequestRef.current = request
    try {
      const forecastingResult = await dispatch(getForecasting({
        affiliateId: selectedAffiliate || undefined,
        contactGroupId: selectedContactGroup || undefined,
      }))
      if (urlModeRef.current && request !== forecastingRequestRef.current) return
      if (forecastingResult?.error) {
        showSnackBar("error", forecastingResult?.payload || 'Failed to fetch forecasting')
      } else if (forecastingResult?.payload?.data) {
        setForecasting(forecastingResult.payload.data)
      }
    } catch (error) {
      if (urlModeRef.current && request !== forecastingRequestRef.current) return
      showSnackBar('error', 'An unexpected error occurred')
    }
    setForecastingLoading(false)
  }, [dispatch, userRole, selectedAffiliate, selectedContactGroup])

  useEffect(() => {
    fetchForecasting()
  }, [fetchForecasting])

  const { group: addressGroup, affiliate: addressAffiliate } = urlView

  useEffect(() => {
    if (!urlMode || !userRole || !optionsReadyFor || optionsReadyFor.group !== selectedContactGroup) return
    const groupSelectable = canSeeContactGroupFilter
      && contactGroups.some((group) => group.value === addressGroup)
    if (optionsReadyFor.groupsLoaded && addressGroup && !groupSelectable) {
      if (canSeeContactGroupFilter) {
        changeDashboardView({ type: 'dropGroup' })
        return
      }
      changeDashboardView({ type: 'dropHiddenGroup' })
    }
    if (addressAffiliate && !canSeeAffiliateFilter) {
      changeDashboardView({ type: 'dropHiddenAffiliate' })
      return
    }
    if (!optionsReadyFor.affiliatesLoaded) return
    const affiliateSelectable = affiliates.some((affiliate) => affiliate.value === addressAffiliate)
    if (addressAffiliate && !affiliateSelectable) changeDashboardView({ type: 'dropAffiliate' })
  }, [
    urlMode, userRole, optionsReadyFor, selectedContactGroup, addressGroup, addressAffiliate, contactGroups, affiliates,
    canSeeContactGroupFilter, canSeeAffiliateFilter, changeDashboardView,
  ])

  const localView = useMemo(() => viewOf({
    period: localDatePreset,
    range: localCustomDateRange,
    group: localSelectedContactGroup,
    affiliate: localSelectedAffiliate,
    metric: localChartMetricType,
    view: localDetailsView,
    day: localDrilldownDay,
    page: localDrilldownPage,
    limit: localDrilldownLimit,
    sort: localDrilldownSortOrder,
  }, { metricOptions }), [
    localDatePreset, localCustomDateRange, localSelectedContactGroup, localSelectedAffiliate, localChartMetricType,
    localDetailsView, localDrilldownDay, localDrilldownPage, localDrilldownLimit, localDrilldownSortOrder, metricOptions,
  ])
  const addressHasView = dashboardUrl.hasView

  useEffect(() => {
    if (!urlMode || seeded) return
    setSeeded(true)
    if (!addressHasView) updateDashboardView(() => localView)
  }, [urlMode, seeded, addressHasView, localView, updateDashboardView])

  // Chart options with the money metrics stripped for reps (see isRep above).
  const chartMetricOptions = deniesMoney
    ? metricOptions.filter(o => !MONEY_METRICS.includes(o.value))
    : metricOptions

  // Get comparison label based on selected date preset
  // The snapshot describes this instant, not the selected period, so it says so where the other
  // cards put their comparison. asOf comes from the server: a client-side clock would claim a
  // freshness the figure does not have.
  const inTransitSubtitle = useMemo(() => {
    const metric = metrics.inTransitPayments
    const count = metric?.current?.count || 0
    const oldest = daysInTransit(metric?.oldestSubmittedAt)
    const asOf = metric?.asOf ? formatBusinessDateTime(metric.asOf) : null

    return [
      ratiosOnly ? 'payments in transit' : `${count} payments`,
      count > 0 && oldest !== EMPTY_CELL ? `oldest ${oldest}` : null,
      asOf ? `as of ${asOf}` : null,
      'not filtered by the period',
    ].filter(Boolean).join(' · ')
  }, [metrics.inTransitPayments, ratiosOnly])

  const inTransitBreakdown = useMemo(
    () => (metrics.inTransitPayments?.byProcessor || []).map((part) => ({
      key: part.key,
      label: part.label,
      value: formatMoney(part.amount || 0),
    })),
    [metrics.inTransitPayments],
  )

  const getComparisonLabel = () => {
    switch (datePreset) {
      case 'today': return 'vs yesterday'
      case 'last7Days': return 'vs prev 7 days'
      case 'last30Days': return 'vs prev 30 days'
      case 'custom': return 'vs prev period'
      default: return ''
    }
  }

  // Percent change of the AVERAGE payment, not of the total amount. Written once: the chart
  // labels this figure per payment, so a money metric missing from here would silently be
  // averaged per day instead — the shape of the number stays plausible, so nobody notices.
  const averagePaymentChange = (metric) => {
    const current = metrics[metric]?.current
    const previous = metrics[metric]?.previous
    if (!current?.count || !previous?.count) return null
    const currentAvg = current.amount / current.count
    const previousAvg = previous.amount / previous.count
    if (previousAvg === 0) return currentAvg > 0 ? 100 : 0
    return Math.round(((currentAvg - previousAvg) / previousAvg) * 1000) / 10
  }

  const getChartPercentChange = () => {
    if (MONEY_METRICS.includes(effectiveChartMetric)) return averagePaymentChange(effectiveChartMetric)

    switch (effectiveChartMetric) {
      case 'newEnrollments':
        return metrics.newEnrollments?.percentChange
      case 'totalCanceled':
        return metrics.totalCanceled?.percentChange
      default:
        return null
    }
  }

  // Denominator for "Average payment amount". Keyed off MONEY_METRICS so a metric can never be
  // money for the label and not money for the arithmetic.
  const getPaymentCount = () => (
    MONEY_METRICS.includes(effectiveChartMetric)
      ? metrics[effectiveChartMetric]?.current?.count || 0
      : 0
  )

  return (
    <div>
      <ContainerPage>
        <div className="flex flex-col">
          <PageTitle title="Dashboard" />

          {/* Filters Section */}
          <div className="flex flex-row flex-wrap gap-4 mb-6 items-end">
            <DateRangeFilter
              datePreset={datePreset}
              setDatePreset={changeDatePreset}
              customDateRange={customDateRange}
              setCustomDateRange={changeCustomDateRange}
            />

            {canSeeContactGroupFilter && contactGroups.length > 0 && (
              <ContactGroupFilter
                selectedGroup={selectedContactGroup}
                setSelectedGroup={changeContactGroup}
                contactGroups={contactGroups}
                loading={loadingDropdowns}
              />
            )}

            {canSeeAffiliateFilter && affiliates.length > 0 && (
              <AffiliateFilter
                selectedAffiliate={selectedAffiliate}
                setSelectedAffiliate={changeAffiliate}
                affiliates={affiliates}
                loading={loadingDropdowns}
              />
            )}
          </div>

          {/* Metrics Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <MetricCard
              title="New Enrollments"
              value={metrics.newEnrollments?.current || 0}
              percentChange={metrics.newEnrollments?.percentChange}
              comparisonLabel={getComparisonLabel()}
              loading={metricsLoading}
              icon={LuUserPlus}
              accent="indigo"
              spark={sparklines.newEnrollments}
              active={effectiveChartMetric === 'newEnrollments'}
              onClick={() => changeChartMetric('newEnrollments')}
              onDrilldown={() => openDrilldown('newEnrollments')}
              drilldownLabel="See the enrollments behind this number"
              info="Clients who activated a payment plan during the selected period."
            />
            {userRole && (!deniesMoney || ratiosOnly) && (
              <MetricCard
                title="Cleared Payments"
                value={ratiosOnly
                  ? (metrics.clearedPayments?.current?.count || 0)
                  : formatMoney(metrics.clearedPayments?.current?.amount || 0)}
                subtitle={ratiosOnly ? 'payments cleared' : `${metrics.clearedPayments?.current?.count || 0} payments`}
                percentChange={ratiosOnly
                  ? metrics.clearedPayments?.percentChange?.count
                  : metrics.clearedPayments?.percentChange?.amount}
                comparisonLabel={getComparisonLabel()}
                loading={metricsLoading}
                icon={LuDollarSign}
                accent="success"
                spark={sparklines.clearedPayments}
                active={effectiveChartMetric === 'clearedPayments'}
                onClick={canChartMetric('clearedPayments') ? () => changeChartMetric('clearedPayments') : undefined}
                onDrilldown={canChartMetric('clearedPayments') ? () => openDrilldown('clearedPayments') : undefined}
                drilldownLabel="See the payments behind this number"
                info={ratiosOnly
                  ? "Payments that successfully settled (cleared) within the selected period."
                  : "Total of payments that successfully settled (cleared) within the selected period."}
              />
            )}
            <MetricCard
              title="Canceled Client"
              value={metrics.totalCanceled?.current || 0}
              subtitle={`Avg ${metrics.averageLifeSpan || 0} payments`}
              absoluteChange={(metrics.totalCanceled?.current || 0) - (metrics.totalCanceled?.previous || 0)}
              invertColors={true}
              comparisonLabel={getComparisonLabel()}
              loading={metricsLoading}
              icon={LuUserX}
              accent="error"
              spark={sparklines.totalCanceled}
              active={effectiveChartMetric === 'totalCanceled'}
              onClick={() => changeChartMetric('totalCanceled')}
              onDrilldown={() => openDrilldown('totalCanceled')}
              drilldownLabel="See the cancellations behind this number"
              info="Clients whose enrollment was canceled during the selected period."
            />
            <MetricCard
              title="Collection Rate"
              {...collectionRateCardProps(metrics)}
              comparisonLabel={getComparisonLabel()}
              loading={metricsLoading}
              icon={LuPercent}
              accent="indigo"
            />
            {userRole && (!deniesMoney || ratiosOnly) && (
            <MetricCard
              title="Returned Payments"
              value={ratiosOnly
                ? (metrics.returnedPayments?.current?.count || 0)
                : formatMoney(metrics.returnedPayments?.current?.amount || 0)}
              subtitle={ratiosOnly
                ? `${metrics.returnedPayments?.current?.nsf || 0} NSF · ${metrics.returnedPayments?.current?.declined || 0} declined`
                : `${metrics.returnedPayments?.current?.count || 0} returned · ${metrics.returnedPayments?.current?.nsf || 0} NSF · ${metrics.returnedPayments?.current?.declined || 0} declined`}
              absoluteChange={(metrics.returnedPayments?.current?.count || 0) - (metrics.returnedPayments?.previous?.count || 0)}
              invertColors={true}
              comparisonLabel={getComparisonLabel()}
              loading={metricsLoading}
              icon={LuUndo2}
              accent="error"
              spark={sparklines.returnedPayments}
              active={effectiveChartMetric === 'returnedPayments'}
              onClick={canChartMetric('returnedPayments') ? () => changeChartMetric('returnedPayments') : undefined}
              onDrilldown={canChartMetric('returnedPayments') ? () => openDrilldown('returnedPayments') : undefined}
              drilldownLabel="See the returned payments behind this number"
              info="Payments that failed after submission - NSF or declined by the bank."
            />
            )}
            {ratiosOnly && (
              <MetricCard
                title="Cleared Ratio"
                value={metrics.clearedRatio != null ? `${Math.round(metrics.clearedRatio * 100)}%` : '—'}
                subtitle="of payments due in the period"
                comparisonLabel={getComparisonLabel()}
                loading={metricsLoading}
                icon={LuPercent}
                accent="success"
                info="Share of the payments that came due in this period which cleared, out of everything that cleared, was returned or is still pending."
              />
            )}
            {ratiosOnly && (
              <MetricCard
                title="Cancellation Ratio"
                value={metrics.cancelledRatio != null ? `${Math.round(metrics.cancelledRatio * 100)}%` : '—'}
                subtitle="of the active book"
                comparisonLabel={getComparisonLabel()}
                loading={metricsLoading}
                icon={LuUserX}
                accent="error"
                info="Clients who cancelled during this period, as a share of them plus the clients still active."
              />
            )}
            {userRole && (!deniesMoney || ratiosOnly) && (
            <MetricCard
              title="In Transit"
              value={ratiosOnly
                ? (metrics.inTransitPayments?.current?.count || 0)
                : formatMoney(metrics.inTransitPayments?.current?.amount || 0)}
              subtitle={inTransitSubtitle}
              breakdown={ratiosOnly ? undefined : inTransitBreakdown}
              loading={metricsLoading}
              icon={LuBarChart3}
              accent="indigo"
              active={effectiveChartMetric === 'inTransitPayments'}
              onClick={canChartMetric('inTransitPayments') ? () => changeChartMetric('inTransitPayments') : undefined}
              onDrilldown={canChartMetric('inTransitPayments') ? () => openDrilldown('inTransitPayments') : undefined}
              drilldownLabel="See the payments still in transit"
              info="Money a processor has accepted and not yet settled. This card is a live snapshot: the date filter does not apply to it, and payments stay here however long they take."
            />
            )}
          </div>

          {/* Metrics Chart */}
          <div className="mt-6">
            <MetricsChart
              data={chartData}
              metricType={effectiveChartMetric}
              onMetricChange={changeChartMetric}
              menuItems={chartMetricOptions}
              percentChange={getChartPercentChange()}
              comparisonLabel={getComparisonLabel()}
              paymentCount={getPaymentCount()}
              error={chartError}
              onRetry={fetchChart}
              view={effectiveView}
              onViewChange={changeDetailsView}
              onDayClick={openDrilldownForDay}
              listSlot={(
                <DrilldownPanel
                  onOpenClient={urlMode
                    ? (clientId) => {
                      saveScroll()
                      navigate(contactPath(clientId))
                    }
                    : (clientId) => navigate(contactPath(clientId))}
                  clientHref={urlMode ? contactPath : undefined}
                  metric={effectiveChartMetric}
                  metricLabel={(METRIC_OPTIONS.find(o => o.value === effectiveChartMetric) || {}).label || 'Records'}
                  periodLabel={PERIOD_LABELS[datePreset] || ''}
                  day={drilldownDay}
                  onClearDay={clearDrilldownDay}
                  rows={drilldownRows}
                  meta={drilldownMeta}
                  loading={drilldownLoading || (fromUrl && !userRole)}
                  error={drilldownError}
                  onRetry={fetchDrilldown}
                  sortOrder={drilldownSortOrder}
                  onSortOrderChange={changeDrilldownSortOrder}
                  page={drilldownPage}
                  limit={drilldownLimit}
                  setPage={changeDrilldownPage}
                  setLimit={changeDrilldownLimit}
                  canExport={!(deniesMoney && MONEY_METRICS.includes(effectiveChartMetric))}
                  showMoney={Boolean(userRole) && !deniesMoney}
                  onMetricChange={changeChartMetric}
                  pending={drilldownPending}
                  pendingHint={rangeIncomplete ? 'Pick both dates to load the records for this range.' : null}
                />
              )}
            />
          </div>

          {showLeaderboard && (
            <div className="mt-6">
              <CreditPullLeaderboard
                data={leaderboard}
                loading={leaderboardLoading || (!leaderboard && !leaderboardError)}
                error={leaderboardError}
                onRetry={fetchLeaderboard}
              />
            </div>
          )}

          {/* Forecasting Section */}
          <div className="mt-8">
            <h2 className="text-md font-semibold text-text-secondary mb-3">Forecasting</h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
              <MetricCard
                title="Average Term"
                value={forecasting.averageTerm != null ? `${forecasting.averageTerm.toFixed(1)} payments` : '—'}
                subtitle={`${forecasting.planCount || 0} active plans`}
                loading={forecastingLoading}
                icon={LuBarChart3}
                accent="indigo"
                info="Average plan term, in payments, across active plans."
              />
            </div>

            <ClearanceFunnelChart
              data={forecasting.clearanceByPosition}
              retentionData={forecasting.retentionByPosition}
            />
          </div>
        </div>
      </ContainerPage>
    </div>
  )
}

export default SalesDashboard
